import { EditCompanyPolicyRequest, ResponseErrorDto } from '@interfaces/models';
import { createEmployeeProfile } from '@api/company-policy';
import { COLORS_KEYS, COLORS } from '@helpers/constant';
import { useGetEmployeeProfiles } from '@shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { useEffect } from 'react';
import { z } from 'zod';

import { useCreateEmployeesProfileAssignEmployees } from './create-employees-profile-assign-employees/use-create-employees-profile-assign-employees';

const employeesProfileFormSchema = (t: TFunction) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) })
      .max(50, { message: t('FORMS.MAX_CHARACTERS', { limit: 50 }) }),
    color: z
      .string()
      .trim()
      .min(1, { message: t('FORMS.REQUIRED') }),
    maxAmount: z
      .number({ coerce: true })
      .min(0, { message: t('FORMS.INVALID_CURRENCY_VALUE', { limit: 0 }) })
      .optional()
      .nullable(),
    validFrom: z.date().optional().nullable(),
    validTo: z.date().optional().nullable(),
    assignedEmployees: z.array(z.string()).optional(),
    services: z
      .object({
        highwaysConfiguration: z.any().optional().nullable(),
        parkingConfiguration: z.any().optional().nullable(),
        publicTransportConfiguration: z.any().optional().nullable(),
      })
      .refine(
        (data) =>
          !data ||
          (data?.highwaysConfiguration !== null && data?.highwaysConfiguration !== undefined) ||
          (data?.parkingConfiguration !== null && data?.parkingConfiguration !== undefined) ||
          (data?.publicTransportConfiguration !== null &&
            data?.publicTransportConfiguration !== undefined),
        { message: t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.AT_LEAST_ONE_SERVICE_REQUIRED') },
      ),
  });

type EmployeesProfileFormData = z.infer<ReturnType<typeof employeesProfileFormSchema>>;

const getRandomColor = (): string =>
  COLORS[COLORS_KEYS[Math.floor(Math.random() * COLORS_KEYS.length)]!];

const DEFAULT_VALUES: EmployeesProfileFormData = {
  assignedEmployees: [],
  color: getRandomColor(),
  maxAmount: null,
  validFrom: null,
  validTo: null,
  services: {
    publicTransportConfiguration: null,
    highwaysConfiguration: null,
    parkingConfiguration: null,
  },
  name: '',
};

export const useCreateEmployeesProfile = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const { refetchEmployeeProfiles } = useGetEmployeeProfiles();
  const { t } = useTranslation();

  const form = useForm<EmployeesProfileFormData>({
    resolver: zodResolver(employeesProfileFormSchema(t)),
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createEmployeeProfile,
    onSuccess: () => {
      toast.success(t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.CREATE_PROFILE_SUCCESS'));
      form.reset({ ...DEFAULT_VALUES, color: getRandomColor() });

      refetchEmployeeProfiles();
      onSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const assignedEmployees = form.watch('assignedEmployees');
  const assignTable = useCreateEmployeesProfileAssignEmployees(assignedEmployees || []);

  const createProfileHandler = (data: EmployeesProfileFormData) => {
    const payload = {
      ...data,
      validFrom: data.validFrom ? new Date(data.validFrom).toISOString() : null,
      validTo: data.validTo ? new Date(data.validTo).toISOString() : null,
      maxAmount: data.maxAmount || null,
    } as EditCompanyPolicyRequest;
    mutate(payload);
  };

  const services = form.watch('services');

  useEffect(() => {
    form.clearErrors('services');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const onSubmit = form.handleSubmit(createProfileHandler);

  return {
    assignedEmployees,
    assignTable,
    isPending,
    onSubmit,
    form,
  };
};
